const PRICE_PARAM_NAMES = ["filter.v.price.gte", "filter.v.price.lte"];

/**
 * The shop's money format minus the currency symbol — the price fields render
 * their own `€`/`kr` next to the input, so only the number belongs inside it.
 * Read at call time, not cached: Shopify Markets localizes `money_format` per
 * presentment currency and the inline script in theme.liquid sets it.
 */
function getPriceAmountFormat() {
    const format =
        (window.Shopify && window.Shopify.money_format) || "{{amount}}";
    const placeholder = format.match(/{{\s*(\w+)\s*}}/);
    return placeholder ? "{{" + placeholder[1] + "}}" : "{{amount}}";
}

/**
 * Render an amount (in the currency's major unit) the way the rest of the store
 * renders prices. noUiSlider's default formatter is locale-blind — it always
 * emitted `4000.00`, which read as wrong next to the "The highest price is
 * €22.000,00" label right above it.
 */
function formatPriceAmount(amount) {
    const number = typeof amount === "number" ? amount : parsePriceAmount(amount);
    if (isNaN(number)) return "";
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(
            Math.round(number * 100),
            getPriceAmountFormat()
        );
    }
    return number.toFixed(2);
}

/**
 * Inverse of formatPriceAmount, tolerant of every separator Shopify can emit
 * plus whatever the shopper typed by hand. A trailing separator followed by one
 * or two digits is the decimal part; every other separator is a thousands
 * separator. `parseFloat` used to stand in for this and silently truncated the
 * non-"." locales — CHF "9'990.00" and SEK "9 990,00" both came back as 9,
 * which collapsed the slider's whole range.
 */
function parsePriceAmount(value) {
    if (typeof value === "number") return value;
    if (value === null || value === undefined) return NaN;
    const cleaned = String(value).replace(/[^\d.,'\s]/g, "").trim();
    if (cleaned === "") return NaN;
    const decimals = cleaned.match(/[.,](\d{1,2})$/);
    const integer = (
        decimals ? cleaned.slice(0, -decimals[0].length) : cleaned
    ).replace(/\D/g, "");
    if (integer === "" && !decimals) return NaN;
    return parseFloat((integer || "0") + (decimals ? "." + decimals[1] : ""));
}

/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
    constructor() {
        this.container = document.getElementById("Facet-Drawer");
        let button = document.getElementById("Facets-Toggle");

        // Add functionality to buttons
        if (button) {
            button.addEventListener("click", (e) => {
                e.preventDefault();
                document
                    .getElementsByTagName("body")[0]
                    .classList.toggle("open-cc");
                this.container.classList.toggle("active");
            });
        }
    }
}

class CustomFacet extends HTMLElement {
    constructor() {
        super();
        window.scrollBy(0, 1);
        this.form = this.closest("form");
        this.input = this.querySelector("input");
        this.button = this.querySelector(".custom-facet--button");
        this.panel = this.querySelector(".custom-facet--list");
        this.group = this.dataset.group ?? "no-group";

        this.count = this.button.querySelector(".custom-facet--count");

        this.isSlider = this.dataset.slider === "true";

        this.ignoreSize = this.form.dataset.ignores === "true";

        this.button.addEventListener("click", this.togglePanel.bind(this));
        this.button.addEventListener("focusout", this.outFocus.bind(this));

        this.panel
            .querySelectorAll(".custom-facet--option")
            .forEach((option) => {
                option.addEventListener("click", this.onOptionClick.bind(this));
            });

        this.panel.querySelectorAll(".custom-facet--item").forEach((option) => {
            option.addEventListener("click", this.onButtonClick.bind(this));
        });

        if (this.isSlider) {
            let inputs = this.panel.querySelectorAll(".field__input");
            inputs.forEach((input) => {
                input.addEventListener("focusout", (e) => {
                    this.button.focus();
                });
            });
        }

        // Bound once so addEventListener/removeEventListener target the same
        // reference across open/close cycles.
        this.repositionPanel = this.positionPanel.bind(this);
    }

    positionPanel() {
        const buttonBox = this.button.getBoundingClientRect();

        // The panel is position:fixed (so it isn't clipped by the bar's
        // horizontal overflow), so it must be placed in viewport
        // coordinates — sitting just below the button that opened it.
        this.panel.style.top = `${buttonBox.bottom}px`;

        // Prevent the panel from going off the right edge of the screen
        if (buttonBox.left + this.panel.offsetWidth > window.innerWidth - 20) {
            this.panel.style.right = "20px";
            this.panel.style.left = "auto";
        } else {
            this.panel.style.left = `${buttonBox.left}px`;
            this.panel.style.right = "auto";
        }
    }

    togglePanel(e) {
        e.preventDefault();
        if (this.classList.contains("custom-facet--active")) {
            this.closePanel();
        } else {
            this.openPanel();
        }
    }

    openPanel() {
        // Close all other panels in the group

        document
            .querySelectorAll(`custom-facet[data-group="${this.group}"]`)
            .forEach((facet) => {
                facet.closePanel();
            });

        // Calculate the panel offset from left on desktop
        if (window.innerWidth > 768 || this.ignoreSize) {
            this.positionPanel();

            // The button sits in a `position: sticky` bar (facets-filter-bar),
            // so while the page scrolls the button can still be moving toward
            // its stuck offset. The panel itself is `position: fixed` and only
            // gets positioned once above, so without this listener it stays
            // put on scroll while the button slides away underneath it.
            // Capture phase so this also fires for scrollable ancestors, not
            // just the window.
            window.addEventListener("scroll", this.repositionPanel, {
                passive: true,
                capture: true,
            });

            this.button.setAttribute("aria-expanded", "true");
            this.classList.add("custom-facet--active");
        } else {
            let drawer = document.getElementById("Facet-Drawer");
            document.getElementsByTagName("body")[0].classList.add("open-cc");

            let items = drawer.querySelectorAll("collapsible-row");
            let scroll_container = drawer.querySelector(
                ".side-panel-down-content"
            );

            items.forEach((item) => {
                item.children[0].removeAttribute("open");
                if (item.dataset.index === this.dataset.index) {
                    item.children[0].setAttribute("open", "true");

                    // Scroll to this element
                    if (scroll_container) {
                        scroll_container.scrollTop = item.offsetTop - 80;
                    }
                }
            });

            drawer.classList.add("active");
        }
    }

    outFocus(e) {
        if (window.innerWidth <= 768) return;
        if (
            !this.contains(e.relatedTarget) &&
            !e.relatedTarget?.classList.contains("facets__form")
        ) {
            this.closePanel();
        } else if (!this.isSlider) {
            this.button.focus();
        }
    }

    closePanel() {
        if (window.innerWidth > 768 || this.ignoreSize) {
            window.removeEventListener("scroll", this.repositionPanel, {
                capture: true,
            });

            this.button.setAttribute("aria-expanded", "false");
            this.classList.remove("custom-facet--active");

            this.panel.style.left = "0";
            this.panel.style.right = "auto";
        } else {
            document
                .getElementsByTagName("body")[0]
                .classList.remove("open-cc");
            document.getElementById("Facet-Drawer").classList.remove("active");
        }
    }

    onOptionClick(e) {
        e.stopPropagation();
        // Update dropdown count
        let inp = e.currentTarget.querySelector("input");

        if (inp.tagName !== "INPUT" || inp.tagName !== "LABEL") return;

        if (inp.checked) {
            this.count.innerHTML = parseInt(this.count.innerHTML) + 1;
        } else {
            this.count.innerHTML = parseInt(this.count.innerHTML) - 1;
        }
        if (this.count.innerHTML === "0") {
            this.count.classList.add("hidden");
        } else {
            this.count.classList.remove("hidden");
        }

        this.input.value = e.currentTarget.dataset.value;
        this.input.dispatchEvent(new Event("change"));
    }

    onButtonClick(e) {
        this.input.value = e.currentTarget.dataset.value;
        this.form.dispatchEvent(new Event("change"));
    }
}

customElements.define("custom-facet", CustomFacet);

class FacetFiltersForm extends HTMLElement {
    constructor() {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 500);

        this.querySelector("form").addEventListener(
            "input",
            this.debouncedOnSubmit.bind(this)
        );

        if (!document.getElementById("facet-dynamic-collection")) return;
    }

    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state
                ? event.state.searchParams
                : FacetFiltersForm.searchParamsInitial;

            if (searchParams === FacetFiltersForm.searchParamsPrev) return;

            FacetFiltersForm.renderPage(searchParams, null, true);
        };
        window.addEventListener("popstate", onHistoryChange);
    }

    static toggleActiveFacets(disable = true) {
        document.querySelectorAll(".js-facet-remove").forEach((element) => {
            element.classList.toggle("disabled", disable);
        });
    }

    static renderPage(searchParams, event, updateURLHash = true) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();
        const container = document.getElementsByClassName("thb-filter-count");
        document
            .getElementById("ProductGridContainer")
            .querySelector(".collection")
            .classList.add("loading");

        for (var item of container) {
            item.classList.add("loading");
        }

        // On the search page the visible count has moved up to the <h1> row,
        // and .thb-filter-count's spinner cannot stand in for it: hiding that
        // label collapses the element to 0x0, which collapses its
        // absolutely-positioned .loading-overlay too. So the h1-row count
        // carries its own loading state, otherwise it just shows a stale
        // number through the input debounce plus the fetch.
        const searchCountEl = document.querySelector(
            ".template-header--search-count"
        );
        if (searchCountEl) {
            searchCountEl.classList.add("loading");
        }

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            if (FacetFiltersForm.filterData.some(filterDataUrl)) {
                FacetFiltersForm.renderSectionFromCache(
                    filterDataUrl,
                    event,
                    searchParams
                );
            } else {
                FacetFiltersForm.renderSectionFromFetch(
                    url,
                    event,
                    searchParams
                );
            }
        });
        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    static renderSectionFromFetch(url, event, searchParams) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FacetFiltersForm.filterData = [
                    ...FacetFiltersForm.filterData,
                    {
                        html,
                        url,
                    },
                ];

                FacetFiltersForm.renderFilters(html, event);
                FacetFiltersForm.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);

                new FacetsToggle();
            });
    }

    static renderSectionFromCache(filterDataUrl, event, searchParams) {
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
    }

    static renderProductGridContainer(html) {
        let newC = new DOMParser().parseFromString(html, "text/html");

        const facets = newC.querySelectorAll("#FacetFiltersForm custom-facet");
        let id = "";
        let st = 0;
        facets.forEach((facet) => {
            let item = document.querySelector(
                `custom-facet[data-index="${facet.dataset.index}"]`
            );
            if (item && item.classList.contains("custom-facet--active")) {
                id = facet.dataset.index;
                st = item.panel.querySelector("ul")?.scrollTop;
            }
        });

        document.getElementById("ProductGridContainer").innerHTML =
            newC.getElementById("ProductGridContainer").innerHTML;

        if (id !== "") {
            let f = document.querySelector(`custom-facet[data-index="${id}"]`);
            let list = f.panel.querySelector("ul");
            if (list) list.scrollTop = st;
            f.button.click();
            f.button.focus();
        }
    }

    static renderProductCount(html) {
        const parsed = new DOMParser().parseFromString(html, "text/html");

        const countHtml = parsed.getElementById("ProductCount");
        const countHtmlMobile = parsed.getElementById("ProductCount-sm");

        if (countHtml) {
            const g = document.getElementById("ProductCount");
            g.innerHTML = countHtml.innerHTML;
            g.classList.remove("loading");
        }

        if (countHtmlMobile) {
            const g = document.getElementById("ProductCount-sm");
            g.innerHTML = countHtmlMobile.innerHTML;
            g.classList.remove("loading");
        }

        // The search page shows "N results found" next to the <h1>, which sits
        // in the template-header section and is therefore never refetched.
        // main-search carries an authoritative, already-pluralized copy, so
        // mirror that across instead of recomputing the string here.
        const searchCount = parsed.getElementById("SearchResultCount");
        const searchCountTarget = document.querySelector(
            ".template-header--search-count"
        );
        if (searchCountTarget) {
            searchCountTarget.classList.remove("loading");
        }
        if (searchCount && searchCountTarget) {
            searchCountTarget.textContent = searchCount.textContent;
        }

        // The zero-results state carries its own centred heading, so
        // template-header bows out — but it does that in Liquid, and
        // template-header is NOT part of the refetched section. Filtering a
        // non-empty result set down to zero would therefore leave the old
        // <h1> on screen next to a freshly-mirrored "0 results found": two
        // headings for one page, contradicting each other. Keep the two in
        // step here. Guarded on #SearchResultCount so collection pages, which
        // never render one, are left alone.
        // Toggle .template-header, not the .section-template-header wrapper:
        // Liquid renders this element in BOTH states (hidden when the search
        // came back empty), so the same node can be hidden and restored. The
        // wrapper has no styles of its own, so hiding the inner div looks the
        // same.
        if (searchCount) {
            const pageHeader = document.querySelector(".template-header");
            if (pageHeader) {
                pageHeader.hidden = parsed.querySelector(".search-empty")
                    ? true
                    : false;
            }
        }
    }

    static renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, "text/html");

        const facetDetailsElements = parsedHTML.querySelectorAll(
            "#FacetFiltersFormMobile collapsible-row"
        );
        const matchesIndex = (element) => {
            const jsFilter = event
                ? event.target.closest("collapsible-row")
                : undefined;
            return jsFilter
                ? element.dataset.index === jsFilter.dataset.index
                : false;
        };
        const facetsToRender = Array.from(facetDetailsElements).filter(
            (element) => !matchesIndex(element)
        );
        const countsToRender =
            Array.from(facetDetailsElements).find(matchesIndex);

        facetsToRender.forEach((element) => {
            let item = document.querySelector(
                `collapsible-row[data-index="${element.dataset.index}"]`
            );
            // data-index is a plain forloop.index, not a stable filter id —
            // when the fetched result set has a different set of facet
            // groups than the currently rendered page (e.g. a group with no
            // remaining options drops out), a fetched row can have no live
            // counterpart. Skip it rather than throwing and aborting the
            // whole render (which left the grid stuck mid-filter).
            if (!item) return;
            if (item.children[0].getAttribute("open")) {
                element.children[0].setAttribute("open", "true");
            }
            item.innerHTML = element.innerHTML;
        });

        FacetFiltersForm.renderActiveFacets(parsedHTML);
        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender)
            FacetFiltersForm.renderCounts(
                countsToRender,
                event.target.closest("collapsible-row")
            );

      window.scrollBy(0, 1);
    }

    static renderActiveFacets(html) {
        const activeFacetElementSelectors = [".active-facets"];

        activeFacetElementSelectors.forEach((selector) => {
            const activeFacetsElement = html.querySelector(selector);
            if (!activeFacetsElement) return;
            document.querySelector(selector).innerHTML =
                activeFacetsElement.innerHTML;
        });

        FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
        const mobileElementSelectors = [".mobile-filter-count"];

        mobileElementSelectors.forEach((selector) => {
            if (!html.querySelector(selector)) return;
            document.querySelector(selector).innerHTML =
                html.querySelector(selector).innerHTML;
        });
    }

    static renderCounts(source, target) {
        const targetElement = target.querySelector(".facets__selected");
        const sourceElement = source.querySelector(".facets__selected");

        if (sourceElement && targetElement) {
            target.querySelector(".facets__selected").outerHTML =
                source.querySelector(".facets__selected").outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState(
            {
                searchParams,
            },
            "",
            `${window.location.pathname}${
                searchParams && "?".concat(searchParams)
            }`
        );
    }

    static getSections() {
        return [
            {
                section: document.getElementById("product-grid").dataset.id,
            },
        ];
    }

    static getFilterCount(name, html) {
        let f;
        if (html) {
            const parsedHTML = new DOMParser().parseFromString(
                html,
                "text/html"
            );
            f = parsedHTML.querySelector(`input[value^="${name}"]`);
        } else {
            f = document.querySelector(`input[value^="${name}"]`);
        }
        if (!f) return false;
        if (f.disabled) {
            return false;
        }
        return true;
    }

    onSubmitHandler(event) {
        event.preventDefault();
        const form = event.target.closest("form");
        const formData = new FormData(form);
        // get current url search params
        const urlSearchParams = new URLSearchParams(window.location.search);

        const searchParams = new URLSearchParams(formData);

        // The price fields show a locale-formatted amount ("4.000,00" in EUR,
        // "9'990.00" in CHF), but Shopify's price filter only understands a
        // plain number. Normalize before comparing bounds or submitting.
        PRICE_PARAM_NAMES.forEach((name) => {
            const raw = searchParams.get(name);
            if (raw === null) return;
            const amount = parsePriceAmount(raw);
            if (isNaN(amount)) {
                searchParams.delete(name);
            } else {
                searchParams.set(name, String(amount));
            }
        });

        if (searchParams.get("filter.v.price.gte") === "0") {
            searchParams.delete("filter.v.price.gte");
        }
        const priceSlider = document.querySelector(".price_slider");
        if (priceSlider) {
            const rangeMax = parsePriceAmount(priceSlider.dataset.max);
            if (
                !isNaN(rangeMax) &&
                searchParams.get("filter.v.price.lte") === String(rangeMax)
            ) {
                searchParams.delete("filter.v.price.lte");
            }
        }

        // snippets/facets-bar.liquid is a form with nothing but the sort
        // control in it, so its submission has to be merged into the current
        // query string or every active filter would be dropped. The facet forms
        // DO carry their filter inputs, and there "no filter params" is a fact,
        // not a gap: it means the shopper just cleared the last one. Merging
        // there resurrected the filter they had removed — unchecking the last
        // box, or dragging the price slider back to its maximum, appeared to do
        // nothing at all.
        const isSortOnlyForm = form.querySelector('[name^="filter."]') === null;

        if (
            isSortOnlyForm &&
            searchParams.get("sort_by") != null &&
            searchParams.size == 1 &&
            urlSearchParams.size > 1
        ) {
            urlSearchParams.set("sort_by", searchParams.get("sort_by"));
            urlSearchParams.delete("page");
            FacetFiltersForm.renderPage(urlSearchParams.toString(), event);
            return;
        }
      
        FacetFiltersForm.renderPage(searchParams.toString(), event);
    }

    onActiveFilterClick(event) {
        event.preventDefault();
        FacetFiltersForm.toggleActiveFacets();
        const url =
            event.currentTarget.href.indexOf("?") == -1
                ? ""
                : event.currentTarget.href.slice(
                      event.currentTarget.href.indexOf("?") + 1
                  );
        FacetFiltersForm.renderPage(url);
    }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
    constructor() {
        super();
        this.querySelectorAll("a").forEach((item) => {
            item.addEventListener("click", (event) => {
                event.preventDefault();
                const form =
                    this.closest("facet-filters-form") ||
                    document.querySelector("facet-filters-form");
                form.onActiveFilterClick(event);
            });
        });
    }
}

customElements.define("facet-remove", FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
    constructor() {
        super();
    }
    connectedCallback() {
        let slider = this.querySelector(".price_slider"),
            amounts = this.querySelector(".price_slider_amount"),
            rangeMax = parsePriceAmount(slider.dataset.max),
            minValue = parsePriceAmount(slider.dataset.minValue),
            maxValue = parsePriceAmount(slider.dataset.maxValue),
            args = {
                start: [
                    isNaN(minValue) ? 0 : minValue,
                    isNaN(maxValue) ? rangeMax : maxValue,
                ],
                connect: true,
                step: 10,
                range: {
                    min: 0,
                    max: rangeMax,
                },
                // Without this noUiSlider writes its own locale-blind "4000.00"
                // into the two visible fields on every update.
                format: {
                    to: formatPriceAmount,
                    from: parsePriceAmount,
                },
            },
            event = new CustomEvent("input"),
            form =
                this.closest("facet-filters-form") ||
                document.querySelector("facet-filters-form");

        if (slider.classList.contains("noUi-target")) {
            slider.noUiSlider.destroy();
        }
        // noUiSlider throws on a non-positive range and takes the rest of the
        // facets script down with it; leave the plain number fields in place.
        if (isNaN(rangeMax) || rangeMax <= 0) return;
        noUiSlider.create(slider, args);

        slider.noUiSlider.on("update", function (values) {
            amounts.querySelector(".field__input_min").value = values[0];
            amounts.querySelector(".field__input_max").value = values[1];
        });
        slider.noUiSlider.on("change", function (values) {
            form.querySelector("form").dispatchEvent(event);
        });
    }
}
customElements.define("price-slider", PriceSlider);

window.addEventListener("load", () => {
    new FacetsToggle();
});
